import * as XLSX from "xlsx";

// Minimal CSV parser supporting quoted fields and commas/semicolons.
export function parseCSV(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  const sep = (text.split("\n")[0] || "").includes(";") ? ";" : ",";
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === sep) { cur.push(field); field = ""; }
      else if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
      else field += c;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  const cleaned = rows.filter(r => r.some(c => c.trim() !== ""));
  if (cleaned.length === 0) return [];
  const headers = cleaned[0].map(h => h.trim().toLowerCase());
  return cleaned.slice(1).map(r => {
    const o: Record<string, string> = {};
    headers.forEach((h, idx) => { o[h] = (r[idx] ?? "").trim(); });
    return o;
  });
}

// Parse an uploaded file (CSV or Excel) into a uniform list of row objects keyed by lower-case header.
export async function parseFixturesFile(file: File): Promise<Record<string, string>[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || file.type === "text/csv") {
    return parseCSV(await file.text());
  }
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: false });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "", raw: false });
  return json.map(row => {
    const o: Record<string, string> = {};
    for (const k of Object.keys(row)) {
      o[k.trim().toLowerCase()] = String(row[k] ?? "").trim();
    }
    return o;
  });
}

// Hard requirements — row cannot be imported without these.
export const REQUIRED_COLS = [
  "tournament_name", "year", "phase",
  "kickoff_at", "home_team", "away_team",
];
// Recommended — missing values produce a warning but don't block import.
export const RECOMMENDED_COLS = ["match_number", "group_name", "stadium", "city"];
export const OPTIONAL_COLS = ["home_score", "away_score", "status", "fifa_match_id", "source_url"];
export const ALL_COLS = [...REQUIRED_COLS, ...RECOMMENDED_COLS, ...OPTIONAL_COLS];
export const VALID_PHASES = ["group", "league_phase", "r32", "playoff", "r16", "qf", "sf", "third", "final"];
export const VALID_STATUSES = ["scheduled", "live", "finished"];

/** Normalize a free-form phase label (e.g. "Group stage", "Round of 16", "Quarterfinals") to a canonical code. */
export function normalizePhase(input: string): string {
  const s = (input || "").trim().toLowerCase();
  if (!s) return "";
  if (VALID_PHASES.includes(s)) return s;
  if (/league\s*phase|ligov[áa]\s*f[áa]ze|md\d/.test(s)) return "league_phase";
  if (/play[\s-]?off|bar[áa]ž|knockout\s*(phase\s*)?play/.test(s)) return "playoff";
  if (/^group(\s|$)|skupin/.test(s)) return "group";
  if (/round\s*of\s*32|r32|šestnáctifin/.test(s)) return "r32";
  if (/round\s*of\s*16|r16|osmifin/.test(s)) return "r16";
  if (/quarter|qf|čtvrtfin/.test(s)) return "qf";
  if (/semi|sf|semifin/.test(s)) return "sf";
  if (/third|3rd|bronze|o\s*třet/.test(s)) return "third";
  if (/final|finále/.test(s)) return "final";
  return s;
}


/** Normalize a free-form status to a canonical code. */
export function normalizeStatus(input: string): string {
  const s = (input || "").trim().toLowerCase();
  if (!s) return "scheduled";
  if (VALID_STATUSES.includes(s)) return s;
  if (/sched|plán|upcoming|tbd/.test(s)) return "scheduled";
  if (/live|in.?play|now/.test(s)) return "live";
  if (/finish|final.?score|ft|done|ukon/.test(s)) return "finished";
  return s;
}

const TEMPLATE_ROWS: Record<string, string>[] = [
  {
    match_number: "1", tournament_name: "FIFA World Cup 2026", year: "2026", phase: "group",
    group_name: "A", kickoff_at: "2026-06-11T20:00:00Z", stadium: "Estadio Azteca", city: "Mexico City",
    home_team: "Mexico", away_team: "TBD", home_score: "", away_score: "", status: "scheduled",
    fifa_match_id: "", source_url: "https://www.fifa.com/en/tournaments/mens/worldcup/canadamexicousa2026/scores-fixtures",
  },
  {
    match_number: "75", tournament_name: "FIFA World Cup 2026", year: "2026", phase: "r16",
    group_name: "", kickoff_at: "2026-06-29T20:00:00Z", stadium: "MetLife Stadium", city: "New York",
    home_team: "Winner Group A", away_team: "Runner-up Group B", home_score: "", away_score: "", status: "scheduled",
    fifa_match_id: "", source_url: "",
  },
  {
    match_number: "101", tournament_name: "FIFA World Cup 2026", year: "2026", phase: "final",
    group_name: "", kickoff_at: "2026-07-19T19:00:00Z", stadium: "MetLife Stadium", city: "New York",
    home_team: "Winner Match 99", away_team: "Winner Match 100", home_score: "", away_score: "", status: "scheduled",
    fifa_match_id: "", source_url: "",
  },
];

export function buildCsvTemplate(): string {
  const escape = (v: string) => /[",;\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  const header = ALL_COLS.join(",");
  const body = TEMPLATE_ROWS.map(r => ALL_COLS.map(c => escape(r[c] ?? "")).join(",")).join("\n");
  return header + "\n" + body + "\n";
}
